import { ChangeEvent, FC } from 'react';

import { EAssetType } from 'constants/enums';
import { GRADIENT_INPUT_STYLE, ROLLING_OFFER_DEFAULTS } from 'views/OfferDesign/OfferDesign.consts';
import { getFieldError, getInputErrorProps } from 'views/OfferDesign/OfferDesign.utils';

import AcGradientInput from 'components/AcGradientInput/AcGradientInput';
import { ERequiredMarkColor } from 'components/AcGradientInput/AcGraidentInput.types';
import Checkbox from 'components/Checkbox/Checkbox';
import Input from 'components/Input/Input';
import { ColorPicker } from 'components/ui/ColorPicker/color-picker';
import { Separator } from 'components/ui/separator';
import UploadImage from 'components/UploadImage/UploadImage';

import { BORDER_WIDTH_MAX, MAIN_UPLOAD_FIELDS, TEXT_FIELDS } from './RollingOfferFormFields.consts';
import { RollingOfferFormFieldsProps } from './RollingOfferFormFields.types';

export const RollingOfferFormFields: FC<RollingOfferFormFieldsProps> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  isNameUnique,
  isExternalIdUnique
}) => {
  const uniqueFlags = { isNameUnique, isExternalIdUnique };
  const backgroundOpacityError = touched.backgroundOpacity ? errors.backgroundOpacity : undefined;
  const borderWidthError = touched.borderWidth ? errors.borderWidth : undefined;

  const handleBorderWidthChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (Number(e.target.value) <= BORDER_WIDTH_MAX) {
      handleChange(e);
    }
  };

  return (
    <div className="rounded-lg border border-base-border p-9">
      <h3 className="text-[20px] font-semibold text-zinc-900">Rolling Offer</h3>
      <p className="font-inter text-base font-normal text-base-mutedForeground mt-1.5 mb-6">
        Customize the rolling offer to fit your store theme.
      </p>

      <div className="flex gap-5">
        {TEXT_FIELDS.map(({ label, name, placeholder, defaultValue, uniqueFlag, maxLength }) => {
          const error = getFieldError(name, touched, errors, uniqueFlags, uniqueFlag);
          return (
            <Input
              key={name}
              label={label}
              isRequired
              name={name}
              placeholder={placeholder}
              value={values[name] ?? defaultValue}
              onChange={handleChange}
              onBlur={handleBlur}
              {...getInputErrorProps(error)}
              className="flex-1"
              maxLength={maxLength}
            />
          );
        })}
      </div>

      <Separator className="mt-5 mb-9 bg-base-border" />

      <h3 className="text-[20px] font-semibold text-zinc-900 mb-6">Main Offer Design</h3>
      <div className="mb-6">
        <Checkbox
          label="Enable Scroll"
          checked={Boolean(values.enableScroll)}
          onCheckedChange={(checked) => setFieldValue('enableScroll', checked)}
        />
      </div>

      <div className="flex gap-5 mt-4">
        {/* TODO: Aligned with Jonathan — replace AcGradientInput (MUI) with new component when available */}
        <div className={`flex-1 ${GRADIENT_INPUT_STYLE}`}>
          <AcGradientInput
            required
            requiredMarkColor={ERequiredMarkColor.Black}
            header="Background Color"
            name="backgroundColor"
            defaultValue={
              typeof values.backgroundColor === 'object' && values.backgroundColor
                ? values.backgroundColor
                : ROLLING_OFFER_DEFAULTS.backgroundColor
            }
            onChange={handleChange}
            setValue={setFieldValue}
            onBlur={handleBlur}
          />
        </div>
        <div className="flex-1">
          <Input
            label="Background opacity"
            isRequired
            name="backgroundOpacity"
            type="number"
            min={0}
            max={100}
            value={Number(values.backgroundOpacity)}
            onChange={handleChange}
            onBlur={handleBlur}
            {...getInputErrorProps(backgroundOpacityError)}
            leftIcon={<span>%</span>}
            leftIconStyles="text-base-foreground text-p-s absolute left-2 top-1/2 -translate-y-1/2"
          />
        </div>
      </div>

      <div className="flex gap-5 mt-4">
        {/* TODO: Aligned with Jonathan — replace AcGradientInput (MUI) with new component when available */}
        <div className={`flex-1 ${GRADIENT_INPUT_STYLE}`}>
          <AcGradientInput
            header="Border Color"
            name="borderColor"
            defaultValue={
              typeof values.borderColor === 'object' && values.borderColor
                ? values.borderColor
                : ROLLING_OFFER_DEFAULTS.borderColor
            }
            onChange={handleChange}
            setValue={setFieldValue}
            onBlur={handleBlur}
          />
        </div>
        <div className="flex-1">
          <Input
            label="Offer border width"
            isRequired
            name="borderWidth"
            type="number"
            min={0}
            max={BORDER_WIDTH_MAX}
            value={Number(values.borderWidth)}
            onChange={handleBorderWidthChange}
            onBlur={handleBlur}
            {...getInputErrorProps(borderWidthError)}
          />
        </div>
      </div>

      <div className="flex gap-5 mt-4">
        <div className="flex-1">
          <ColorPicker
            label="Default Arrows Color"
            defaultValue={values.arrowColor || ROLLING_OFFER_DEFAULTS.arrowColor}
            onChange={(color) => setFieldValue('arrowColor', color)}
          />
        </div>
        <div className="flex-1" />
      </div>

      {MAIN_UPLOAD_FIELDS.map(({ title, fieldName, forceType, tooltip, disabled }) => {
        const isDisabled = disabled?.(values) ?? false;
        return (
          <div key={fieldName} className={`mt-5${isDisabled ? ' opacity-50 pointer-events-none' : ''}`}>
            <UploadImage
              title={title}
              className='w-full !flex flex-row px-5'
              onUpload={(url) => setFieldValue(fieldName, url)}
              selectedImageUrl={values[fieldName] as string}
              forceType={forceType}
              TooltipContent={() => <span>{tooltip}</span>}
            />
          </div>
        );
      })}

      <h3 className="text-[20px] font-semibold text-zinc-900 mt-14 mb-6">Sub Offer Design</h3>

      <div className="mt-4">
        {/* TODO: Aligned with Jonathan — replace AcGradientInput (MUI) with new component when available */}
        <div className={GRADIENT_INPUT_STYLE}>
          <AcGradientInput
            required
            requiredMarkColor={ERequiredMarkColor.Black}
            header="Offers background color"
            name="offersBackgroundColor"
            defaultValue={
              typeof values.offersBackgroundColor === 'object' && values.offersBackgroundColor
                ? values.offersBackgroundColor
                : ROLLING_OFFER_DEFAULTS.subOfferBackgroundColor
            }
            onChange={handleChange}
            setValue={setFieldValue}
            onBlur={handleBlur}
          />
        </div>
      </div>

      <div className="mt-5">
        <UploadImage
          title="Offers background image"
          className="w-full !flex flex-row px-5"
          onUpload={(url) => setFieldValue('offersBackgroundImage', url)}
          selectedImageUrl={values.offersBackgroundImage}
          forceType={EAssetType.BG_BUNDLE}
          TooltipContent={() => <span>Upload an image for sub-offer background.</span>}
        />
      </div>
    </div>
  );
};
